import { Chess } from 'chess.js';
import type { Role, Square } from '@domain/chess/types';
import type { CheckmateCategory, CheckmateFilter } from './kinds';

export interface CheckmatePuzzle {
  category: CheckmateCategory;
  name: string;
  /** Position one move before mate. White to move. */
  fen: string;
  from: Square;
  to: Square;
  san: string;
  promotion?: Role;
}

function kingSquare(game: Chess, color: 'w' | 'b'): Square | null {
  for (const row of game.board()) {
    for (const cell of row) {
      if (cell?.type === 'k' && cell.color === color) return cell.square as Square;
    }
  }
  return null;
}

function opponentInCheck(game: Chess): boolean {
  const attacker = game.turn();
  const defender = attacker === 'w' ? 'b' : 'w';
  const king = kingSquare(game, defender);
  return king !== null && game.isAttacked(king, attacker);
}

function puzzle(
  category: CheckmateCategory,
  name: string,
  fen: string,
  from: Square,
  to: Square,
  promotion?: Role,
): CheckmatePuzzle {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    throw new Error(`${name}: invalid FEN ${fen}`);
  }
  if (game.turn() !== 'w') throw new Error(`${name}: not white to move`);
  if (game.isCheck()) throw new Error(`${name}: white is in check`);
  if (opponentInCheck(game)) throw new Error(`${name}: black is already in check`);
  const start = game.fen();
  let mv: { san: string } | null = null;
  try {
    mv = game.move({ from, to, promotion });
  } catch {
    mv = null;
  }
  if (!mv) throw new Error(`${name}: ${from}${to} is illegal in ${start}`);
  if (!game.isCheckmate()) {
    throw new Error(`${name}: ${mv.san} is not checkmate (${game.fen()})`);
  }
  return { category, name, fen: start, from, to, san: mv.san, promotion };
}

function catalog(entries: Array<() => CheckmatePuzzle>): CheckmatePuzzle[] {
  const out: CheckmatePuzzle[] = [];
  const errors: string[] = [];
  for (const make of entries) {
    try {
      out.push(make());
    } catch (e) {
      errors.push(e instanceof Error ? e.message : String(e));
    }
  }
  if (errors.length) throw new Error(errors.join('\n'));
  return out;
}

/** One typical mate-in-one for every named checkmate pattern. */
export const CHECKMATE_CATALOG: readonly CheckmatePuzzle[] = catalog([
  () => puzzle(
    'opening-traps',
    "Fool's Mate",
    'rnbqkbnr/ppppp2p/5p2/6p1/3PP3/8/PPP2PPP/RNBQKBNR w KQkq - 0 3',
    'd1',
    'h5',
  ),
  () => puzzle(
    'opening-traps',
    "Scholar's Mate",
    'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
    'h5',
    'f7',
  ),
  () => puzzle(
    'opening-traps',
    "Légal's Mate",
    'r2q1bnr/ppp1kBpp/2np4/4N3/4P3/2N5/PPPP2PP/R1BbK2R w KQ - 1 8',
    'c3',
    'd5',
  ),
  () => puzzle(
    'back-rank',
    'Back-Rank Mate',
    '6k1/5ppp/8/8/8/8/5PPP/3R2K1 w - - 0 1',
    'd1',
    'd8',
  ),
  () => puzzle(
    'back-rank',
    'Blind Swine Mate',
    '5rk1/1R5R/8/8/8/8/5PPP/6K1 w - - 0 1',
    'b7',
    'g7',
  ),
  () => puzzle(
    'back-rank',
    "Anderssen's Mate",
    '6k1/6P1/5K2/8/8/8/8/7R w - - 0 1',
    'h1',
    'h8',
  ),
  () => puzzle(
    'back-rank',
    "Mayet's Mate",
    '5rk1/5p2/8/8/8/8/1B6/6KR w - - 0 1',
    'h1',
    'h8',
  ),
  () => puzzle(
    'back-rank',
    'Opera Mate',
    '4kb1r/3R1p2/8/6B1/8/8/5PPP/6K1 w - - 0 1',
    'd7',
    'd8',
  ),
  () => puzzle(
    'back-rank',
    "Morphy's Mate",
    '7k/7p/4B3/8/6R1/6p1/8/6K1 w - - 0 1',
    'g4',
    'g8',
  ),
  () => puzzle(
    'back-rank',
    "Pillsbury's Mate",
    '5rk1/5p1R/6p1/4B3/8/8/8/6K1 w - - 0 1',
    'h7',
    'h8',
  ),
  () => puzzle(
    'rook-knight',
    'Arabian Mate',
    '7k/R7/5N2/8/8/8/8/6K1 w - - 0 1',
    'a7',
    'h7',
  ),
  () => puzzle(
    'rook-knight',
    "Anastasia's Mate",
    '8/4N1pk/8/R7/8/8/8/6K1 w - - 0 1',
    'a5',
    'h5',
  ),
  () => puzzle(
    'rook-knight',
    'Hook Mate',
    'R7/4kp2/5N2/4P3/8/8/8/6K1 w - - 0 1',
    'a8',
    'e8',
  ),
  () => puzzle(
    'rook-knight',
    "Vuković's Mate",
    '7k/4NR2/6K1/8/8/8/8/8 w - - 0 1',
    'f7',
    'h7',
  ),
  () => puzzle(
    'rook-knight',
    'Corner Mate',
    '7k/7p/8/4N3/8/8/6R1/6K1 w - - 0 1',
    'e5',
    'f7',
  ),
  () => puzzle(
    'rook-knight',
    "Stamma's Mate",
    '6rk/8/3N3K/8/8/8/8/8 w - - 0 1',
    'd6',
    'f7',
  ),
  () => puzzle(
    'queen',
    'Epaulette Mate',
    '5rkr/8/Q7/8/8/8/8/6K1 w - - 0 1',
    'a6',
    'g6',
  ),
  () => puzzle(
    'queen',
    'Dovetail Mate',
    '8/8/8/8/6p1/5qk1/8/6KQ w - - 0 1',
    'h1',
    'h2',
  ),
  () => puzzle(
    'queen',
    "Swallow's Tail Mate",
    '3rkr2/3n1n2/Q7/8/8/8/8/6K1 w - - 0 1',
    'a6',
    'e6',
  ),
  () => puzzle(
    'queen',
    "Damiano's Mate",
    '5rk1/6p1/6P1/7Q/8/8/8/6K1 w - - 0 1',
    'h5',
    'h7',
  ),
  () => puzzle(
    'queen',
    "Damiano's Bishop Mate",
    '5k2/5p2/4Q1B1/8/8/8/8/6K1 w - - 0 1',
    'e6',
    'f7',
  ),
  () => puzzle(
    'queen',
    "Lolli's Mate",
    '6k1/5p2/5PpQ/8/8/8/8/6K1 w - - 0 1',
    'h6',
    'g7',
  ),
  () => puzzle(
    'queen',
    "Max Lange's Mate",
    '7k/5ppp/8/7Q/8/3B4/8/6K1 w - - 0 1',
    'h5',
    'h7',
  ),
  () => puzzle(
    'queen',
    'Balestra Mate',
    '6k1/8/7Q/5B2/8/8/8/6K1 w - - 0 1',
    'f5',
    'e6',
  ),
  () => puzzle(
    'queen',
    'Kill Box Mate',
    '8/7R/k7/2Q5/8/8/8/6K1 w - - 0 1',
    'h7',
    'a7',
  ),
  () => puzzle(
    'queen',
    'Triangle Mate',
    '7k/6R1/Q7/8/8/8/8/6K1 w - - 0 1',
    'a6',
    'h6',
  ),
  () => puzzle(
    'queen',
    'Kiss of Death',
    '6k1/Q7/5P2/8/8/8/8/6K1 w - - 0 1',
    'a7',
    'g7',
  ),
  () => puzzle(
    'two-bishops',
    "Boden's Mate",
    '2kr4/3p4/8/1B6/5B2/8/8/6K1 w - - 0 1',
    'b5',
    'a6',
  ),
  () => puzzle(
    'two-bishops',
    'Double Bishop Mate',
    '7k/7p/8/3B4/1B6/8/8/6K1 w - - 0 1',
    'b4',
    'c3',
  ),
  () => puzzle(
    'two-bishops',
    "Blackburne's Mate",
    '5rk1/8/6B1/6N1/8/8/1B6/6K1 w - - 0 1',
    'g6',
    'h7',
  ),
  () => puzzle(
    'two-bishops',
    "Greco's Mate",
    '7k/6p1/8/Q7/2B5/8/8/6K1 w - - 0 1',
    'a5',
    'h5',
  ),
  () => puzzle(
    'two-bishops',
    "Réti's Mate",
    'rnb2b1r/ppk2ppp/2p5/6B1/4n3/8/PPP2PPP/2KR1B1R w - - 0 11',
    'g5',
    'd8',
  ),
  () => puzzle(
    'knight',
    'Smothered Mate',
    '6rk/6pp/8/6N1/8/8/8/6K1 w - - 0 1',
    'g5',
    'f7',
  ),
  () => puzzle(
    'knight',
    'Suffocation Mate',
    '6rk/6p1/6B1/6N1/8/8/8/6K1 w - - 0 1',
    'g5',
    'f7',
  ),
  () => puzzle(
    'knight',
    'Double Knight Mate',
    'k7/pp6/2N5/3N4/8/8/8/6K1 w - - 0 1',
    'd5',
    'c7',
  ),
  () => puzzle(
    'heavy',
    'Ladder Mate',
    '6k1/1R6/8/8/8/8/5PPP/R5K1 w - - 0 1',
    'a1',
    'a8',
  ),
  () => puzzle(
    'heavy',
    'Railroad Mate',
    '7k/R7/6Q1/8/8/8/5PPP/6K1 w - - 0 1',
    'a7',
    'h7',
  ),
  () => puzzle(
    'heavy',
    'Box Mate',
    '4k3/8/4K3/8/8/8/8/R7 w - - 0 1',
    'a1',
    'a8',
  ),
  () => puzzle(
    'pawn',
    'David and Goliath Mate',
    '6nk/6p1/6KP/8/8/8/8/8 w - - 0 1',
    'h6',
    'g7',
  ),
  () => puzzle(
    'pawn',
    'Pawn Mate',
    '6k1/4P3/6K1/8/8/8/8/8 w - - 0 1',
    'e7',
    'e8',
    'q',
  ),
  () => puzzle(
    'endgame',
    'Queen Mate',
    '4k3/8/4K3/8/8/8/8/Q7 w - - 0 1',
    'a1',
    'a8',
  ),
  () => puzzle(
    'endgame',
    'Rook Mate',
    '3k4/8/3K4/8/8/8/8/7R w - - 0 1',
    'h1',
    'h8',
  ),
  () => puzzle(
    'endgame',
    'Two Bishops Mate',
    '7k/8/6K1/8/5B2/8/B7/8 w - - 0 1',
    'f4',
    'e5',
  ),
  () => puzzle(
    'endgame',
    'Bishop and Knight Mate',
    '7k/4B3/6KN/8/8/8/8/8 w - - 0 1',
    'e7',
    'f6',
  ),
  () => puzzle(
    'endgame',
    'Two Knights Mate',
    '6rk/8/7K/5NN1/8/8/8/8 w - - 0 1',
    'g5',
    'f7',
  ),
]);

export function checkmatesFor(filter: CheckmateFilter): readonly CheckmatePuzzle[] {
  if (filter === 'all') return CHECKMATE_CATALOG;
  return CHECKMATE_CATALOG.filter((p) => p.category === filter);
}

export function nextCheckmatePuzzle(
  filter: CheckmateFilter,
  opts?: { excludeFen?: string },
): CheckmatePuzzle | null {
  const pool = checkmatesFor(filter);
  if (pool.length === 0) return null;
  const rest = opts?.excludeFen
    ? pool.filter((p) => p.fen !== opts.excludeFen)
    : pool;
  const choices = rest.length > 0 ? rest : pool;
  const i = Math.floor(Math.random() * choices.length);
  return choices[i] ?? null;
}

export function pieceEndsOn(
  start: Square,
  moves: readonly { from: Square; to: Square }[],
): Square | null {
  let sq: Square | null = start;
  for (const m of moves) {
    if (sq === null) break;
    if (m.from === sq) sq = m.to;
    else if (m.to === sq) sq = null;
  }
  return sq;
}
